import { SuccessStory, ISuccessStory } from '@app/models/SuccessStory'
import { ListingType } from '@app/models/enums'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface ISuccessStoriesListState {
  readonly error?: string
  readonly filterText: string
  readonly items: List<ISuccessStory>
  readonly isLoading: boolean
  readonly orderBy: string
  readonly sortOrder: string
  readonly totalItems: number
}

const SuccessStoriesListStateRecord = Record({
  error: undefined,
  filterText: '',
  items: List<ISuccessStory>(),
  isLoading: false,
  orderBy: 'Ascending',
  sortOrder: 'DateCreated',
  totalItems: 0,
})

export class SuccessStoriesListState
  extends SuccessStoriesListStateRecord
  implements ISuccessStoriesListState
{
  public static fromJS(initialState?: Partial<ISuccessStoriesListState>) {
    if (!initialState) {
      return new SuccessStoriesListState()
    }
    const { items, ...rest } = initialState
    return new SuccessStoriesListState({
      items: items ? List(items.map((i) => SuccessStory.fromJS(i))) : undefined,
      ...rest,
    })
  }

  public readonly error?: string
  public readonly filterText: string
  public readonly items: List<ISuccessStory>
  public readonly isLoading: boolean
  public readonly orderBy: string
  public readonly sortOrder: string
  public readonly totalItems: number

  constructor(props?: Partial<SuccessStoriesListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum SuccessStoriesListActionTypes {
  LoadSuccessStories = 'SUCCESS_STORY_LIST::LOAD_SUCCESS_STORY',
  LoadSuccessStoriesFailure = 'SUCCESS_STORY_LIST::LOAD_SUCCESS_STORY_FAILURE',
  LoadSuccessStoriesRequest = 'SUCCESS_STORY_LIST::LOAD_SUCCESS_STORY_REQUEST',
  LoadSuccessStoriesSuccess = 'SUCCESS_STORY_LIST::LOAD_SUCCESS_STORY_SUCCESS',
  SetFilterText = 'SUCCESS_STORY_LIST::SET_FILTER_TEXT',
  SetSortOrder = 'SUCCESS_STORY_LIST::SET_SORT_ORDER',
  SetOrderBy = 'SUCCESS_STORY_LIST::SET_ORDER_BY',
}

interface ILoadSuccessStoriesAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: SuccessStoriesListActionTypes.LoadSuccessStories
}

interface ILoadSuccessStoriesFailureAction {
  error: string
  type: SuccessStoriesListActionTypes.LoadSuccessStoriesFailure
}

interface ILoadSuccessStoriesRequestAction {
  type: SuccessStoriesListActionTypes.LoadSuccessStoriesRequest
}

interface ILoadSuccessStoriesSuccessAction {
  append: boolean
  items: SuccessStory[]
  totalItems: number
  type: SuccessStoriesListActionTypes.LoadSuccessStoriesSuccess
}

interface ISetSortOrderAction {
  sortOrder: string
  type: SuccessStoriesListActionTypes.SetSortOrder
}

interface ISetOrderByAction {
  orderBy: string
  type: SuccessStoriesListActionTypes.SetOrderBy
}

interface ISetFilterTextAction {
  filterText: string
  type: SuccessStoriesListActionTypes.SetFilterText
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' successStories contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadSuccessStoriesFailureAction
  | ILoadSuccessStoriesRequestAction
  | ILoadSuccessStoriesSuccessAction
  | ISetFilterTextAction
  | ISetSortOrderAction
  | ISetOrderByAction

// -------------------------
// ACTION CREATORS
// -------------------------

function loadSuccessStories(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadSuccessStoriesAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: SuccessStoriesListActionTypes.LoadSuccessStories,
  }
}

function loadSuccessStoriesFailure(
  error: string,
): ILoadSuccessStoriesFailureAction {
  return {
    error,
    type: SuccessStoriesListActionTypes.LoadSuccessStoriesFailure,
  }
}

function loadSuccessStoriesRequest(): ILoadSuccessStoriesRequestAction {
  return {
    type: SuccessStoriesListActionTypes.LoadSuccessStoriesRequest,
  }
}

function loadSuccessStoriesSuccess(
  items: SuccessStory[],
  append: boolean,
  totalItems: number,
): ILoadSuccessStoriesSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: SuccessStoriesListActionTypes.LoadSuccessStoriesSuccess,
  }
}

function setFilterText(filterText: string): ISetFilterTextAction {
  return {
    filterText,
    type: SuccessStoriesListActionTypes.SetFilterText,
  }
}

function setOrderBy(orderBy: string): ISetOrderByAction {
  return {
    orderBy,
    type: SuccessStoriesListActionTypes.SetOrderBy,
  }
}

function setSortOrder(sortOrder: string): ISetSortOrderAction {
  return {
    sortOrder,
    type: SuccessStoriesListActionTypes.SetSortOrder,
  }
}

export const actionCreators = {
  loadSuccessStories,
  setFilterText,
  setOrderBy,
  setSortOrder,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<SuccessStoriesListState> = (
  state: SuccessStoriesListState = new SuccessStoriesListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case SuccessStoriesListActionTypes.SetFilterText:
      return state.set(
        'filterText',
        action.filterText,
      ) as SuccessStoriesListState

    case SuccessStoriesListActionTypes.SetOrderBy:
      return state.set('orderBy', action.orderBy) as SuccessStoriesListState

    case SuccessStoriesListActionTypes.SetSortOrder:
      return state.set('sortOrder', action.sortOrder) as SuccessStoriesListState

    case SuccessStoriesListActionTypes.LoadSuccessStoriesFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as SuccessStoriesListState

    case SuccessStoriesListActionTypes.LoadSuccessStoriesRequest:
      return state.set('isLoading', true) as SuccessStoriesListState

    case SuccessStoriesListActionTypes.LoadSuccessStoriesSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<SuccessStory>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as SuccessStoriesListState
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------

function* loadSuccessStoriesSaga(action: ILoadSuccessStoriesAction) {
  try {
    yield put(loadSuccessStoriesRequest())

    const currentState: SuccessStoriesListState = yield select(
      (state: ApplicationState) => state.successStoriesList,
    )

    const query: string[] = []

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    if (currentState.filterText) {
      query.push(`filter=${currentState.filterText}`)
    }

    query.push(`sortOrder=${currentState.sortOrder}`)

    query.push(`orderBy=${currentState.orderBy}`)

    const url = `/api/SuccessStories${
      query.length > 0 ? `?${query.join('&')}` : ''
    }`

    const result = yield makeAuthenticatedRequest(url)
    if (result.success === true) {
      const successStories: SuccessStory[] = result.results.map((c: any) =>
        SuccessStory.fromJS(c),
      )
      yield put(
        loadSuccessStoriesSuccess(
          successStories,
          action.append,
          result.paging.total,
        ),
      )
    } else {
      yield put(loadSuccessStoriesFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadSuccessStoriesFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadSuccessStories() {
  yield takeLatest(
    SuccessStoriesListActionTypes.LoadSuccessStories,
    loadSuccessStoriesSaga,
  )
}

export function* successStoriesListRootSaga() {
  yield fork(watchLoadSuccessStories)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState =
  () =>
  (state: ApplicationState): SuccessStoriesListState =>
    state.successStoriesList

export const selectItems = () =>
  createSelector(selectModuleState(), (mState) => mState.items)

export const selectTotalItems = () =>
  createSelector(selectModuleState(), (mState) => mState.totalItems)

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectFilterText = () =>
  createSelector(selectModuleState(), (mState) => mState.filterText)

export const selectOrderBy = () =>
  createSelector(selectModuleState(), (mState) => mState.orderBy)

export const selectSortOrder = () =>
  createSelector(selectModuleState(), (mState) => mState.sortOrder)
